import axiosInstance from '../../axios/axiosInstance'
import store from '../store'
import {
  SET_WORDS_LIST,
  SET_WORDS_LOADING,
  SET_WORDS_FILTERS,
  PATCH_WORD_MASTERY,
  PATCH_WORDS_MASTERY_BULK,
  SET_AUTO_MASTERY_UPDATES,
  SET_WORDS_PAGE,
  SET_WORDS_SELECTION,
  SET_WORDS_SELECT_ALL_MATCHING,
  CLEAR_WORDS_CACHE,
  SET_WORDS_STATS,
} from '../types'
import { filtersKey, WordsLevelFilters } from '../reducers/wordsReducer'

export type MasteryThree = 'unknown' | 'learning' | 'mastered'

interface FetchParams {
  page: number
  perPage: number
  search?: string
  mastery?: string[]
  levels?: WordsLevelFilters
  struggling?: boolean
}

type WordsFilterState = {
  search: string
  mastery: string[]
  levels: WordsLevelFilters
  struggling: boolean
}

const inflight = new Set<string>()

const emptyLevels = (): WordsLevelFilters => ({ hsk: [], newHsk: [], tofcl: [] })

const appendLevelParams = (query: URLSearchParams, levels?: WordsLevelFilters) => {
  if (!levels) return
  if (levels.hsk?.length) query.set('hsk', levels.hsk.join(','))
  if (levels.newHsk?.length) query.set('newHsk', levels.newHsk.join(','))
  if (levels.tofcl?.length) query.set('tofcl', levels.tofcl.join(','))
}

const buildKey = (p: FetchParams): string => {
  const m = (p.mastery || []).slice().sort().join(',')
  const lv = p.levels || emptyLevels()
  const h = [...lv.hsk].sort().join(',')
  const nh = [...lv.newHsk].sort().join(',')
  const t = [...lv.tofcl].sort().join(',')
  return `s=${p.search || ''}|m=${m}|h=${h}|nh=${nh}|t=${t}|st=${p.struggling ? 1 : 0}|p=${p.page}|pp=${p.perPage}`
}

const fetchPage = async (params: FetchParams, filters: WordsFilterState) => {
  const key = buildKey(params)
  if (inflight.has(key)) return
  inflight.add(key)
  try {
    const query = new URLSearchParams()
    query.set('page', String(params.page))
    query.set('perPage', String(params.perPage))
    if (params.search) query.set('search', params.search)
    if (params.mastery?.length) query.set('mastery', params.mastery.join(','))
    if (params.struggling) query.set('struggling', 'true')
    appendLevelParams(query, params.levels)
    const res = await axiosInstance.get(`user-language-dictionary/my-words?${query}`)
    const data = res?.data?.data
    if (data) {
      store.dispatch({
        type: SET_WORDS_LIST,
        payload: { ...data, filters },
      })
    }
    return data
  } finally {
    inflight.delete(key)
  }
}

export const listMyWords = async (params: FetchParams) => {
  const filters: WordsFilterState = {
    search: params.search || '',
    mastery: params.mastery || [],
    levels: params.levels || emptyLevels(),
    struggling: !!params.struggling,
  }
  const state = store.getState().words
  const cacheKey = `${filtersKey(filters)}|p=${params.page}`
  const cached = state.cache[cacheKey]

  if (!cached) {
    store.dispatch({ type: SET_WORDS_LOADING, payload: true })
  }

  try {
    const data = await fetchPage(params, filters)
    return data
  } catch (error) {
    store.dispatch({ type: SET_WORDS_LOADING, payload: false })
  }
}

export const prefetchPage = (params: FetchParams) => {
  const filters: WordsFilterState = {
    search: params.search || '',
    mastery: params.mastery || [],
    levels: params.levels || emptyLevels(),
    struggling: !!params.struggling,
  }
  const state = store.getState().words
  const cacheKey = `${filtersKey(filters)}|p=${params.page}`
  if (state.cache[cacheKey]) return
  void fetchPage(params, filters).catch(() => undefined)
}

// Stats deliberately ignore the mastery filter so the progress bar stays
// stable while the user toggles mastery chips. Search + level filters DO
// affect stats (they redefine the population being counted).
const statsInflight = new Set<string>()
export const listMyWordsStats = async (params: { search?: string; levels?: WordsLevelFilters }) => {
  const lv = params.levels || emptyLevels()
  const key = `s=${params.search || ''}|h=${[...lv.hsk].sort()}|nh=${[...lv.newHsk].sort()}|t=${[...lv.tofcl].sort()}`
  if (statsInflight.has(key)) return
  statsInflight.add(key)
  store.dispatch({ type: SET_WORDS_STATS, payload: { loading: true } })
  try {
    const query = new URLSearchParams()
    if (params.search) query.set('search', params.search)
    appendLevelParams(query, params.levels)
    const res = await axiosInstance.get(`user-language-dictionary/my-words/stats?${query}`)
    const data = res?.data?.data
    if (data) {
      store.dispatch({
        type: SET_WORDS_STATS,
        payload: { stats: data.stats, total: data.total, loading: false },
      })
    } else {
      store.dispatch({ type: SET_WORDS_STATS, payload: { loading: false } })
    }
    return data
  } catch {
    store.dispatch({ type: SET_WORDS_STATS, payload: { loading: false } })
  } finally {
    statsInflight.delete(key)
  }
}

export const setWordsPage = (page: number) => {
  const { filters } = store.getState().words
  store.dispatch({ type: SET_WORDS_PAGE, payload: { page, filters } })
}

export const setWordsSelection = (selectedIds: string[]) => {
  store.dispatch({ type: SET_WORDS_SELECTION, payload: selectedIds })
}

export const setSelectAllMatching = (on: boolean) => {
  store.dispatch({ type: SET_WORDS_SELECT_ALL_MATCHING, payload: on })
}

export const clearWordsCache = () => {
  store.dispatch({ type: CLEAR_WORDS_CACHE })
}

export const setWordMastery = async (wordId: string, mastery: MasteryThree) => {
  const prev = store.getState().words.items.find((w: any) => w.wordId === wordId)
  store.dispatch({ type: PATCH_WORD_MASTERY, payload: { wordId, mastery } })
  try {
    await axiosInstance.post('user-language-dictionary/mastery', { wordId, mastery })
    const { filters } = store.getState().words
    void listMyWordsStats({ search: filters.search || undefined, levels: filters.levels })
  } catch {
    if (prev) store.dispatch({ type: PATCH_WORD_MASTERY, payload: { wordId, mastery: prev.mastery } })
    throw new Error('Failed to update mastery')
  }
}

export const bulkSetWordMastery = async (wordIds: string[], mastery: MasteryThree) => {
  if (!wordIds.length) return
  const prevById = new Map<string, MasteryThree>()
  store.getState().words.items.forEach((w: any) => {
    if (wordIds.includes(w.wordId)) prevById.set(w.wordId, w.mastery)
  })

  store.dispatch({ type: PATCH_WORDS_MASTERY_BULK, payload: { wordIds, mastery } })

  try {
    await axiosInstance.post('user-language-dictionary/mastery/bulk', { wordIds, mastery })
    store.dispatch({ type: SET_WORDS_SELECTION, payload: [] })
    // Bulk updates can shift which items belong to filtered views and which
    // page a given word lands on — drop the cache so navigation re-fetches fresh.
    store.dispatch({ type: CLEAR_WORDS_CACHE })
    const { page, perPage, filters } = store.getState().words
    void fetchPage(
      {
        page,
        perPage,
        search: filters.search || undefined,
        mastery: filters.mastery.length ? filters.mastery : undefined,
        levels: filters.levels,
        struggling: filters.struggling || undefined,
      },
      filters,
    )
    // Bulk mastery changes shift the 5-tier distribution — refresh the progress bar.
    void listMyWordsStats({ search: filters.search || undefined, levels: filters.levels })
  } catch (err) {
    const byMastery = new Map<MasteryThree, string[]>()
    prevById.forEach((m, id) => {
      const list = byMastery.get(m) || []
      list.push(id)
      byMastery.set(m, list)
    })
    byMastery.forEach((ids, m) => {
      store.dispatch({ type: PATCH_WORDS_MASTERY_BULK, payload: { wordIds: ids, mastery: m } })
    })
    throw new Error('Failed to bulk update mastery')
  }
}

// Bulk-set mastery for EVERY word matching the active filters (search + levels
// + mastery), across all pages. Backs the "select all N matching" action so a
// user can mark a whole level as known without paginating. The set is resolved
// server-side from the filters, so no IDs are sent. We can't optimistically
// patch words on other pages, so we clear the cache and refetch.
export const bulkSetWordMasteryByFilter = async (filters: WordsFilterState, mastery: MasteryThree) => {
  const body: Record<string, string> = { targetMastery: mastery }
  if (filters.search) body.search = filters.search
  if (filters.struggling) body.struggling = 'true'
  if (filters.mastery.length) body.mastery = filters.mastery.join(',')
  if (filters.levels.hsk.length) body.hsk = filters.levels.hsk.join(',')
  if (filters.levels.newHsk.length) body.newHsk = filters.levels.newHsk.join(',')
  if (filters.levels.tofcl.length) body.tofcl = filters.levels.tofcl.join(',')

  store.dispatch({ type: SET_WORDS_LOADING, payload: true })
  try {
    const res = await axiosInstance.post('user-language-dictionary/mastery/bulk-by-filter', body)
    store.dispatch({ type: SET_WORDS_SELECTION, payload: [] })
    store.dispatch({ type: SET_WORDS_SELECT_ALL_MATCHING, payload: false })
    store.dispatch({ type: CLEAR_WORDS_CACHE })
    const { page, perPage, filters: f } = store.getState().words
    await fetchPage(
      {
        page,
        perPage,
        search: f.search || undefined,
        mastery: f.mastery.length ? f.mastery : undefined,
        levels: f.levels,
        struggling: f.struggling || undefined,
      },
      f,
    )
    void listMyWordsStats({ search: f.search || undefined, levels: f.levels })
    return res?.data?.data
  } catch (err) {
    store.dispatch({ type: SET_WORDS_LOADING, payload: false })
    throw new Error('Failed to bulk update mastery by filter')
  }
}

export const updateAutoMasteryUpdates = async (on: boolean) => {
  try {
    const res = await axiosInstance.patch('user-language/auto-mastery/update-auto-mastery-updates', {
      auto_mastery_updates: on,
    })
    store.dispatch({ type: SET_AUTO_MASTERY_UPDATES, payload: res?.data?.data?.auto_mastery_updates })
    return res?.data
  } catch (err) {
    console.log('updateAutoMasteryUpdates api call fail')
    throw err
  }
}

export const setWordsFilters = (filters: {
  search?: string
  mastery?: string[]
  levels?: Partial<WordsLevelFilters>
  struggling?: boolean
}) => {
  store.dispatch({ type: SET_WORDS_FILTERS, payload: filters })
}
